import React, { useCallback, useEffect, useState } from 'react';
import { apiService } from '../services/apiService';
import { DSAlert, DSButton } from '../design-system/primitives';
import PinGolfScore from './PinGolfScore';
import { formatHandicap } from '../utils/pingolfScoring';
import './PinGolfWeekSettlement.css';

// What a finalized league week did: where the money went, which badges it awarded, and the
// results it settled, with the server's consistency checks on top. Finalizing writes all of this
// but nothing showed it afterwards, so an organizer could not see who was paid or confirm the
// settlement was right. League managers only - the endpoint enforces it.

const formatDollars = (value) => (value === null || value === undefined ? '-' : `$${Number(value)}`);

const formatFinalizedAt = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const cardLink = (row) => (row.event_id && row.card_id ? `/events/${row.event_id}/pingolf-cards/${row.card_id}` : null);

function PinGolfWeekSettlement({ weekId }) {
  const [settlement, setSettlement] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const response = await apiService.getPingolfWeekSettlement(weekId);
      setSettlement(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load the settlement for this week.');
    }
  }, [weekId]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) {
    return (
      <div className="pl-admin-empty">
        <DSAlert variant="error" appearance="outline">{error}</DSAlert>
        <DSButton size="sm" variant="secondary" onClick={load}>Try again</DSButton>
      </div>
    );
  }

  if (!settlement) {
    return <p className="pl-admin-empty">Loading settlement...</p>;
  }

  const awardByUserId = new Map(settlement.payouts.map(row => [row.user_id, row.award_amount]));
  const finalizedAt = formatFinalizedAt(settlement.finalized_at);

  return (
    <div className="pg-settlement">
      <p className="pg-settlement-meta">
        Finalized{finalizedAt ? ` ${finalizedAt}` : ''}{settlement.finalized_by ? ` by ${settlement.finalized_by}` : ''}
      </p>

      {settlement.checks.length === 0 ? (
        <DSAlert variant="success" appearance="outline">
          All checks pass: the order matches each net, every payout matches its place and credit, and no card has changed since finalizing.
        </DSAlert>
      ) : (
        <DSAlert variant="error" appearance="outline">
          <strong>{settlement.checks.length} problem{settlement.checks.length === 1 ? '' : 's'} with this settlement</strong>
          <ul className="pg-settlement-checks">
            {settlement.checks.map(message => <li key={message}>{message}</li>)}
          </ul>
        </DSAlert>
      )}

      <dl className="pg-settlement-money">
        <div><dt>Cards sold</dt><dd>{formatDollars(settlement.total_collected)}</dd></div>
        <div><dt>To season pool</dt><dd>{formatDollars(settlement.season_pool_accrual)}</dd></div>
        <div><dt>Weekly pot</dt><dd>{formatDollars(settlement.weekly_pot)}</dd></div>
        <div><dt>Paid out</dt><dd>{formatDollars(settlement.total_awards)}</dd></div>
        <div><dt>House (rounding)</dt><dd>{formatDollars(settlement.house_remainder)}</dd></div>
      </dl>

      <div className="pg-settlement-block">
        <div className="pl-admin-week-section-title">Payouts</div>
        {settlement.payouts.filter(row => row.award_amount > 0).length === 0 ? (
          <p className="pl-admin-empty">Nobody was paid this week.</p>
        ) : (
          <ul className="pg-settlement-list">
            {settlement.payouts.filter(row => row.award_amount > 0).map(row => (
              <li key={row.user_id}>
                <span>#{row.place} {row.name}</span>
                <strong>{formatDollars(row.award_amount)}</strong>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="pg-settlement-block">
        <div className="pl-admin-week-section-title">Badges awarded</div>
        {settlement.badges.length === 0 ? (
          <p className="pl-admin-empty">No badges were awarded by this week.</p>
        ) : (
          <ul className="pg-settlement-list">
            {settlement.badges.map(row => (
              <li key={`${row.user_id}-${row.badge_key}`}>
                <span>{row.name}</span>
                <span>{[row.icon, row.badge_name].filter(Boolean).join(' ')}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="pg-settlement-block">
        <div className="pl-admin-week-section-title">Results ({settlement.results.length})</div>
        <div className="pg-settlement-scroll">
          <table className="pg-settlement-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Player</th>
                <th>Card</th>
                <th>Hcp</th>
                <th>Net</th>
                <th>Pts</th>
                <th>Paid</th>
              </tr>
            </thead>
            <tbody>
              {settlement.results.map(row => {
                const link = cardLink(row);
                return (
                  <tr key={row.user_id}>
                    <td>{row.placement}</td>
                    <th scope="row">
                      {link ? <a className="pl-admin-link" href={link} target="_blank" rel="noreferrer">{row.name}</a> : row.name}
                    </th>
                    <td><PinGolfScore value={row.card_result} /></td>
                    <td>{formatHandicap(row.playing_handicap)}</td>
                    <td><PinGolfScore value={row.net} emphasis /></td>
                    <td>{row.weekly_points}</td>
                    <td>{awardByUserId.get(row.user_id) ? formatDollars(awardByUserId.get(row.user_id)) : ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default PinGolfWeekSettlement;
